import React, { useState, useEffect } from 'react';
import { RefreshCcw, Eye, AlertCircle, TrendingUp, Calendar, Briefcase, Clock } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Stack, Inline } from '@/components/ui/layout';
import { Heading, Text } from '@/components/ui/typography';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export default function Dashboard() {
  const { isSuperAdmin } = useAuth();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/dashboard-stats', {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 401) {
          throw new Error('Not authenticated. Please login as admin first.');
        }
        throw new Error(errorData.error || 'Failed to fetch dashboard statistics');
      }

      const data = await response.json();
      console.log('Dashboard stats loaded:', data);
      setStats(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const pieData = [
    { name: 'Shortlisted', value: stats.shortlistedCandidates, color: '#16a34a' },
    { name: 'Rejected', value: stats.rejectedCandidates, color: '#dc2626' },
    { name: 'Inactive', value: stats.inactiveCandidates, color: '#6b7280' },
  ].filter(item => item.value > 0);

  const lineChartData = stats.lineChartData?.length > 0 ? stats.lineChartData : [{ date: 'No Data', applications: 0 }];

  if (loading) return (
    <div className="p-6 min-h-screen bg-muted/30 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-center py-12">
        <Text>Loading...</Text>
      </div>
    </div>
  );
  if (error) return (
    <div className="p-6 min-h-screen bg-muted/30 max-w-[1600px] mx-auto">
      <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
        <Text className="text-destructive mb-2">{error}</Text>
        <Button onClick={fetchDashboardStats} variant="outline" size="sm">Retry</Button>
      </div>
    </div>
  );

  // Super Admin Dashboard
  if (isSuperAdmin) {
    return (
      <div className="p-6 min-h-screen bg-muted/30 max-w-[1600px] mx-auto space-y-6">
        {/* HEADER METRICS */}
        <Card className="shadow-sm border border-border">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-6 flex-wrap gap-5">
              <Card className="relative bg-gradient-to-br from-primary to-primary/80 rounded-lg p-6 min-w-[150px] flex-1 shadow-lg">
                <CardContent className="p-0">
                  <Text className="text-xs text-white/90 mb-1">Total Jobs</Text>
                  <Text className="text-3xl font-bold text-white">{stats.totalJobs || 0}</Text>
                  <RefreshCcw className="absolute top-2 right-2 w-4 h-4 text-white/80 cursor-pointer hover:text-white" onClick={fetchDashboardStats} />
                </CardContent>
              </Card>
              <div className="text-center">
                <Text className="block text-sm text-muted-foreground">Total Companies</Text>
                <Text className="block text-2xl font-bold">{stats.totalCompanies || 0}</Text>
              </div>
              <div className="text-center">
                <Text className="block text-sm text-muted-foreground">Interviews Completed</Text>
                <Text className="block text-2xl font-bold">{stats.interviewsCompleted || 0}</Text>
              </div>
              <div className="text-center">
                <Text className="block text-sm text-muted-foreground">Total Candidates</Text>
                <Text className="block text-2xl font-bold">{stats.totalCandidates || 0}</Text>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={lineChartData}>
                <XAxis dataKey="date" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Line type="monotone" dataKey="applications" stroke="#2563eb" strokeWidth={3} dot={{ fill: '#2563eb', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* CANDIDATE DISTRIBUTION */}
        <Card className="shadow-sm border border-border">
          <CardHeader>
            <CardTitle>Candidate Distribution ({stats.totalCandidates || 0})</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2}>
                    {pieData.map((e, i) => (<Cell key={i} fill={e.color} />))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center py-12">
                <Text variant="muted">No data available</Text>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-sm border border-border">
          <CardHeader>
            <CardTitle>Recent Companies ({stats.recentCompanies?.length || 0})</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.recentCompanies?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="p-2.5 text-left font-semibold">Company</th>
                      <th className="p-2.5 text-left font-semibold">Created On</th>
                      <th className="p-2.5 text-left font-semibold">Jobs</th>
                      <th className="p-2.5 text-left font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentCompanies.map((row, i) => (
                      <tr key={i} className="border-b hover:bg-muted/50 transition-colors">
                        <td className="p-2.5">{row.company}</td>
                        <td className="p-2.5">{row.createdOn}</td>
                        <td className="p-2.5">{row.jobsPosted}</td>
                        <td className="p-2.5">
                          <Eye className="w-4 h-4 text-primary cursor-pointer" onClick={() => window.location.href = `/company-admin-home?companyId=${row.id}`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center py-12">
                <Text variant="muted">No companies</Text>
              </div>
            )}
          </CardContent>
        </Card>

        {/* TOP TEST RESULTS */}
        {stats.recentTestResults?.length > 0 && (
          <div className="admin-dash-card">
            <h3 className="admin-dash-card-header">
              <span>Top Test Results ({stats.recentTestResults.length})</span>
            </h3>
            <div className="admin-dash-scrollable">
              {stats.recentTestResults.map((test, i) => (
                <div key={i} className="admin-dash-activity-item">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#111827', marginBottom: '4px' }}>{test.candidateName}</div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>{test.jobTitle} • {test.company}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                      {test.testDate} • 
                      <span className={`admin-dash-badge ${test.status === 'Shortlisted' ? 'admin-dash-badge-green' : test.status === 'Rejected' ? 'admin-dash-badge-red' : 'admin-dash-badge-gray'}`} style={{ marginLeft: '8px' }}>
                        {test.status}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>Interview</div>
                      <div className={`admin-dash-badge ${test.overallScore >= 70 ? 'admin-dash-badge-green' : test.overallScore >= 50 ? 'admin-dash-badge-yellow' : 'admin-dash-badge-red'}`} style={{ fontSize: '15px', fontWeight: '700', padding: '6px 12px' }}>
                        {test.overallScore}%
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>Resume</div>
                      <div className={`admin-dash-badge ${test.resumeScore >= 70 ? 'admin-dash-badge-green' : test.resumeScore >= 50 ? 'admin-dash-badge-yellow' : 'admin-dash-badge-red'}`} style={{ fontSize: '15px', fontWeight: '700', padding: '6px 12px' }}>
                        {test.resumeScore}%
                      </div>
                    </div>
                    <Eye className="admin-dash-icon admin-dash-icon-blue" onClick={() => window.location.href = `/company-admin-home?jobId=${test.jobId}`} title="View Details" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Company Admin Dashboard
  return (
    <div className="p-6 min-h-screen bg-muted/30 max-w-[1600px] mx-auto space-y-6">
      {/* MY ASSIGNMENTS */}
      <Card className="shadow-sm border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase size={20} /> My Assignments
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <Card className="bg-gradient-to-br from-primary to-primary/80 text-white shadow-lg">
              <CardContent className="p-6">
                <Text className="text-sm text-white/90 mb-1">Assigned Companies</Text>
                <Text className="text-3xl font-bold text-white">{stats.assignedCompanies || 0}</Text>
                <Text className="text-sm text-white/80 mt-1">Companies managed</Text>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg">
              <CardContent className="p-6">
                <Text className="text-sm text-white/90 mb-1">Individual JDs</Text>
                <Text className="text-3xl font-bold text-white">{stats.assignedJobs || 0}</Text>
                <Text className="text-sm text-white/80 mt-1">Directly assigned</Text>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-lg">
              <CardContent className="p-6">
                <Text className="text-sm text-white/90 mb-1">Total Managing</Text>
                <Text className="text-3xl font-bold text-white">{stats.totalJobsManaging || 0}</Text>
                <Text className="text-sm text-white/80 mt-1">Active positions</Text>
              </CardContent>
            </Card>
          </div>

          {stats.companyBreakdown?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
              {stats.companyBreakdown.map((c, i) => (
                <Card key={i} className="p-4 border border-border hover:shadow-md transition-shadow">
                  <CardContent className="p-0">
                    <Heading level="h4" className="text-base font-semibold mb-2">{c.companyName}</Heading>
                    <Inline spacing="sm" wrap>
                      <span className="px-2 py-1 rounded text-xs font-semibold bg-primary/10 text-primary">{c.totalJobs} jobs</span>
                      <span className="px-2 py-1 rounded text-xs font-semibold bg-green-500/10 text-green-600">{c.totalApplications} apps</span>
                    </Inline>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* THIS WEEK & JOB HEALTH & PERFORMANCE */}
      {/* <div className="admin-dash-grid-3">
        <div className="admin-dash-card">
          <h3 className="admin-dash-card-header"><TrendingUp size={20} /> This Week</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ background: '#f3f4f6', padding: '12px', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>Applications</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827' }}>{stats.applicationsThisWeek || 0}</div>
            </div>
            <div style={{ background: '#f0fdf4', padding: '12px', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#166534' }}>Shortlisted</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#16a34a' }}>{stats.shortlistedThisWeek || 0}</div>
            </div>
          </div>
        </div>

        <div className="admin-dash-card">
          <h3 className="admin-dash-card-header"><AlertCircle size={20} /> Job Health</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ background: '#fef2f2', padding: '12px', borderRadius: '8px', border: '1px solid #fecaca' }}>
              <div style={{ fontSize: '12px', color: '#991b1b' }}>No Applications</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#dc2626' }}>{stats.jobsWithNoApplications || 0}</div>
            </div>
            <div style={{ background: '#f0fdf4', padding: '12px', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#166534' }}>Active (Last 7d)</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#16a34a' }}>{stats.activeJobsLast7Days || 0}</div>
            </div>
            <div style={{ background: '#fef3c7', padding: '12px', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#92400e' }}>Awaiting Interview</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#d97706' }}>{stats.candidatesAwaitingInterview || 0}</div>
            </div>
          </div>
        </div>

        <div className="admin-dash-card">
          <h3 className="admin-dash-card-header"><Clock size={20} /> Performance</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ background: '#eff6ff', padding: '10px', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#1e40af' }}>Shortlist Rate</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2563eb' }}>{stats.shortlistRate || 0}%</div>
            </div>
            <div style={{ background: '#fef2f2', padding: '10px', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#991b1b' }}>Rejection Rate</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626' }}>{stats.rejectionRate || 0}%</div>
            </div>
            <div style={{ background: '#f0fdf4', padding: '10px', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#166534' }}>Interview Conv.</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#16a34a' }}>{stats.interviewConversionRate || 0}%</div>
            </div>
            <div style={{ background: '#fef3c7', padding: '10px', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#92400e' }}>Avg Review</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#d97706' }}>{stats.avgReviewTime || 0}d</div>
            </div>
          </div>
        </div>
      </div> */}

      {/* URGENT - Awaiting Decision */}
      {stats.awaitingShortlistDecision?.length > 0 && (
        <Card className="shadow-sm border-l-4 border-l-destructive">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <AlertCircle size={20} className="text-destructive" />
                Awaiting Shortlist Decision ({stats.awaitingShortlistDecision.length})
              </CardTitle>
              <Text variant="muted" className="text-xs font-medium">Action required</Text>
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-[450px] overflow-y-auto space-y-0">
              {stats.awaitingShortlistDecision.map((cand, i) => (
                <div key={i} className="p-4 flex justify-between items-center border-b hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <Heading level="h4" className="text-sm font-semibold mb-1">{cand.candidateName}</Heading>
                    <Text variant="muted" className="text-sm">{cand.jobTitle} • {cand.company}</Text>
                    <Text variant="muted" className="text-xs mt-1">
                      Interviewed: {cand.interviewDate} <span className="text-destructive font-semibold">({cand.daysSinceInterview}d ago)</span>
                    </Text>
                  </div>
                  <Inline spacing="sm" align="center">
                    <div className="text-center">
                      <Text variant="muted" className="text-xs font-semibold mb-1">Combined</Text>
                      <div className={cn(
                        "px-3 py-1.5 rounded text-base font-bold",
                        cand.combinedScore >= 70 ? "bg-green-500/10 text-green-600" :
                        cand.combinedScore >= 50 ? "bg-yellow-500/10 text-yellow-600" :
                        "bg-red-500/10 text-red-600"
                      )}>
                        {cand.combinedScore}%
                      </div>
                    </div>
                    <div className="text-center">
                      <Text variant="muted" className="text-xs font-semibold mb-1">Interview</Text>
                      <div className="px-2 py-1 rounded text-sm bg-muted text-muted-foreground">{cand.overallScore}%</div>
                    </div>
                    <div className="text-center">
                      <Text variant="muted" className="text-xs font-semibold mb-1">Resume</Text>
                      <div className="px-2 py-1 rounded text-sm bg-muted text-muted-foreground">{cand.resumeScore}%</div>
                    </div>
                    <Eye className="w-4 h-4 text-primary cursor-pointer" onClick={() => window.location.href = `/company-admin-home?jobId=${cand.jobId}`} title="View Details" />
                  </Inline>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ready to Send to Client */}
      {stats.readyToSendToClient?.length > 0 && (
        <Card className="shadow-sm border-l-4 border-l-green-500">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp size={20} className="text-green-500" />
                Ready to Send to Client ({stats.readyToSendToClient.length})
              </CardTitle>
              <Text variant="muted" className="text-xs font-medium">Shortlisted</Text>
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-[450px] overflow-y-auto space-y-0">
              {stats.readyToSendToClient.map((cand, i) => (
                <div key={i} className="p-4 flex justify-between items-center border-b hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <Heading level="h4" className="text-sm font-semibold mb-1">{cand.candidateName}</Heading>
                    <Text variant="muted" className="text-sm">{cand.jobTitle} • {cand.company}</Text>
                    <Text className="text-xs text-green-600 font-semibold mt-1">Shortlisted {cand.daysSinceShortlist}d ago</Text>
                  </div>
                  <Inline spacing="sm" align="center">
                    <div className="text-center">
                      <Text variant="muted" className="text-xs font-semibold mb-1">Interview</Text>
                      <div className="px-3 py-1.5 rounded text-base font-bold bg-green-500/10 text-green-600">{cand.overallScore}%</div>
                    </div>
                    <div className="text-center">
                      <Text variant="muted" className="text-xs font-semibold mb-1">Resume</Text>
                      <div className="px-3 py-1.5 rounded text-base font-bold bg-green-500/10 text-green-600">{cand.resumeScore}%</div>
                    </div>
                    <Eye className="w-4 h-4 text-primary cursor-pointer" onClick={() => window.location.href = `/company-admin-home?jobId=${cand.jobId}`} title="View Profile" />
                  </Inline>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Interview Scheduled */}
      {stats.interviewScheduled?.length > 0 && (
        <Card className="shadow-sm border-l-4 border-l-amber-500">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Clock size={20} className="text-amber-500" />
                Interview Scheduled ({stats.interviewScheduled.length})
              </CardTitle>
              <Text variant="muted" className="text-xs font-medium">Awaiting completion</Text>
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-[450px] overflow-y-auto space-y-0">
              {stats.interviewScheduled.map((cand, i) => (
                <div key={i} className="p-4 flex justify-between items-center border-b hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <Heading level="h4" className="text-sm font-semibold mb-1">{cand.candidateName}</Heading>
                    <Text variant="muted" className="text-sm">{cand.jobTitle} • {cand.company}</Text>
                    <Text className="text-xs text-amber-600 font-semibold mt-1">Waiting {cand.daysWaiting}d</Text>
                  </div>
                  <Inline spacing="sm" align="center">
                    <div className="text-center">
                      <Text variant="muted" className="text-xs font-semibold mb-1">Resume</Text>
                      <div className={cn(
                        "px-3 py-1.5 rounded text-base font-bold",
                        cand.resumeScore >= 70 ? "bg-green-500/10 text-green-600" : "bg-yellow-500/10 text-yellow-600"
                      )}>
                        {cand.resumeScore}%
                      </div>
                    </div>
                    <Eye className="w-4 h-4 text-primary cursor-pointer" onClick={() => window.location.href = `/company-admin-home?jobId=${cand.jobId}`} title="View Details" />
                  </Inline>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Needs Interview Scheduling */}
      {stats.needsInterviewScheduling?.length > 0 && (
        <Card className="shadow-sm border-l-4 border-l-primary">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar size={20} className="text-primary" />
                Needs Interview Scheduling ({stats.needsInterviewScheduling.length})
              </CardTitle>
              <Text variant="muted" className="text-xs font-medium">High scores</Text>
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-[450px] overflow-y-auto space-y-0">
              {stats.needsInterviewScheduling.map((cand, i) => (
                <div key={i} className="p-4 flex justify-between items-center border-b hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <Heading level="h4" className="text-sm font-semibold mb-1">{cand.candidateName}</Heading>
                    <Text variant="muted" className="text-sm">{cand.jobTitle} • {cand.company}</Text>
                    <Text className="text-xs text-primary font-semibold mt-1">Applied: {cand.appliedAt} ({cand.daysPending}d ago)</Text>
                  </div>
                  <Inline spacing="sm" align="center">
                    <div className="text-center">
                      <Text variant="muted" className="text-xs font-semibold mb-1">Resume</Text>
                      <div className={cn(
                        "px-3 py-1.5 rounded text-base font-bold",
                        cand.resumeScore >= 70 ? "bg-green-500/10 text-green-600" : "bg-yellow-500/10 text-yellow-600"
                      )}>
                        {cand.resumeScore}%
                      </div>
                    </div>
                    <Eye className="w-4 h-4 text-primary cursor-pointer" onClick={() => window.location.href = `/company-admin-home?jobId=${cand.jobId}`} title="Schedule Interview" />
                  </Inline>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Test Results */}
      {stats.recentTestResults?.length > 0 && (
        <Card className="shadow-sm border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp size={20} />
              Top Test Results ({stats.recentTestResults.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-[450px] overflow-y-auto space-y-0">
              {stats.recentTestResults.map((test, i) => (
                <div key={i} className="p-4 flex justify-between items-center border-b hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <Heading level="h4" className="text-sm font-semibold mb-1">{test.candidateName}</Heading>
                    <Text variant="muted" className="text-sm">{test.jobTitle} • {test.company}</Text>
                    <Text variant="muted" className="text-xs mt-1">
                      {test.testDate} • 
                      <span className={cn(
                        "ml-2 px-2 py-0.5 rounded text-xs font-semibold",
                        test.status === 'Shortlisted' ? "bg-green-500/10 text-green-600" :
                        test.status === 'Rejected' ? "bg-red-500/10 text-red-600" :
                        "bg-muted text-muted-foreground"
                      )}>
                        {test.status}
                      </span>
                    </Text>
                  </div>
                  <Inline spacing="sm" align="center">
                    <div className="text-center">
                      <Text variant="muted" className="text-xs font-semibold mb-1">Interview</Text>
                      <div className={cn(
                        "px-3 py-1.5 rounded text-base font-bold",
                        test.overallScore >= 70 ? "bg-green-500/10 text-green-600" :
                        test.overallScore >= 50 ? "bg-yellow-500/10 text-yellow-600" :
                        "bg-red-500/10 text-red-600"
                      )}>
                        {test.overallScore}%
                      </div>
                    </div>
                    <div className="text-center">
                      <Text variant="muted" className="text-xs font-semibold mb-1">Resume</Text>
                      <div className={cn(
                        "px-3 py-1.5 rounded text-base font-bold",
                        test.resumeScore >= 70 ? "bg-green-500/10 text-green-600" :
                        test.resumeScore >= 50 ? "bg-yellow-500/10 text-yellow-600" :
                        "bg-red-500/10 text-red-600"
                      )}>
                        {test.resumeScore}%
                      </div>
                    </div>
                    <Eye className="w-4 h-4 text-primary cursor-pointer" onClick={() => window.location.href = `/company-admin-home?jobId=${test.jobId}`} title="View Details" />
                  </Inline>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ANALYTICS CHARTS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="shadow-sm border border-border">
          <CardHeader>
            <CardTitle>Applications Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={lineChartData}>
                <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                <Tooltip />
                <Line type="monotone" dataKey="applications" stroke="#2563eb" strokeWidth={3} dot={{ fill: '#2563eb', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="shadow-sm border border-border">
          <CardHeader>
            <CardTitle>Candidate Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2}>
                    {pieData.map((e, i) => (<Cell key={i} fill={e.color} />))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center py-12">
                <Text variant="muted">No data available</Text>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
